import { imageBucket } from '$lib/supabase';
import { getUser } from '$lib/utils/user';
import { handleAlert } from '$lib/components/alert';
import imageCompression from 'browser-image-compression';
import { get } from 'svelte/store';
import { profileStore } from '$lib/user';

// Helper to create a secure hash of the user ID
const hashUserId = async (userId: string): Promise<string> => {
	try {
		const response = await fetch('/api/hash-user-id', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ userId })
		});

		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.error || 'Failed to hash user ID');
		}

		const { hashedId } = await response.json();
		return hashedId;
	} catch (error) {
		if (error instanceof Error) {
			handleAlert({ type: 'error', text: error.message });
		} else {
			handleAlert({ type: 'error', text: 'Error hashing user ID' });
		}
		throw error;
	}
};

// Helper to get the optimal image URL with transformations
const getOptimizedImageUrl = (path: string, width: number = 800): string => {
	// If path is a full URL, extract the relative path after /object/public/images/
	if (path.startsWith('http')) {
		const match = path.match(/\/object\/public\/images\/([^?]+)/);
		if (match && match[1]) {
			path = match[1];
		} else {
			// fallback: return the original URL if we can't extract the path
			return path;
		}
	}
	// Remove any query params from the path
	path = path.split('?')[0];

	// Now path is always relative and clean
	const {
		data: { publicUrl }
	} = imageBucket('images').getPublicUrl(path, {
		transform: {
			width,
			quality: 95,
			resize: 'contain'
		}
	});
	// console.log('Supabase transformed publicUrl:', publicUrl);
	return publicUrl;
};

// Helper to check if user has required role
const hasImageAccessRole = (): boolean => {
	const profile = get(profileStore);
	const roles = ['grants', 'media', 'admin'];
	return roles.includes(profile?.system_role || '');
};

// Helper to get full resolution image URL
export const getFullResolutionImageUrl = (imagePath: string): string | null => {
	if (!hasImageAccessRole()) {
		handleAlert({
			type: 'error',
			text: 'You do not have permission to access full resolution images'
		});
		return null;
	}

	try {
		// Extract the path from the optimized URL if it's a full URL
		const pathMatch = imagePath.match(/\/storage\/v1\/object\/public\/images\/([^?]+)/);
		const storagePath = pathMatch ? pathMatch[1] : imagePath;

		// Always use the object endpoint for the original file
		const {
			data: { publicUrl }
		} = imageBucket('images').getPublicUrl(storagePath);
		// Force the URL to use /object/public/ and strip any query params
		return publicUrl.replace('/render/image/', '/object/public/').split('?')[0];
	} catch (error) {
		if (error instanceof Error) {
			handleAlert({ type: 'error', text: error.message });
		} else {
			handleAlert({ type: 'error', text: 'Error accessing full resolution image' });
		}
		return null;
	}
};

export const handleImageUpload = async (file: File) => {
	try {
		// Determine maxSizeMB based on user's localbloom
		const profile = get(profileStore);
		const lowBandwidthBlooms = ['Liberia', 'Uganda - Jinja', 'Watamu, Kenya'];
		let maxSizeMB = 5;
		if (profile?.localbloom && lowBandwidthBlooms.includes(profile.localbloom)) {
			maxSizeMB = 2;
		}

		// Compress image before upload
		const options = {
			maxSizeMB,
			maxWidthOrHeight: 2560 // Keep high quality originals
		};
		const compressedFile = await imageCompression(file, options);

		// Generate unique filename with hashed user folder structure
		const userId = getUser().id;
		// console.log('User ID:', userId);
		const hashedUserId = await hashUserId(userId);
		// console.log('Hashed User ID:', hashedUserId);
		const timestamp = Date.now();
		const fileExt = file.name.split('.').pop();
		// Store in hashed user folder: posts/{hashedUserId}/{timestamp}.{ext}
		const filePath = `posts/${hashedUserId}/${timestamp}.${fileExt}`;
		// console.log('File name for upload:', filePath);

		// Upload to Supabase with cache control headers
		const { error: uploadError } = await imageBucket('images').upload(filePath, compressedFile, {
			upsert: false, // Don't overwrite existing files
			contentType: file.type, // Preserve original content type
			cacheControl: 'public, max-age=31536000, immutable' // Cache for 1 year, immutable
		});
		if (uploadError) throw uploadError;

		// Return only the relative path for storage in DB and use everywhere
		return filePath;
	} catch (error) {
		console.error('Error in handleImageUpload:', error);
		if (error instanceof Error) {
			handleAlert({ type: 'error', text: error.message });
		} else {
			handleAlert({ type: 'error', text: 'An unknown error occurred during image upload' });
		}
		return null;
	}
};

// Helper function to get responsive image URLs for different sizes
export const getResponsiveImageUrls = (path: string) => {
	// Remove any query params from the path before generating responsive URLs
	if (path.includes('?')) {
		path = path.split('?')[0];
	}
	// Define common breakpoint widths
	const widths = [400, 800, 1200, 1600];

	// Generate URLs for each width
	return widths.map((width) => ({
		width,
		url: getOptimizedImageUrl(path, width)
	}));
};

export const getPublicImageUrl = (path: string) => {
	const {
		data: { publicUrl }
	} = imageBucket('images').getPublicUrl(path);
	return publicUrl;
};
